import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControlLabel,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Slider,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SaveIcon from '@mui/icons-material/Save';
import LaunchIcon from '@mui/icons-material/Launch';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import ImageIcon from '@mui/icons-material/Image';

const DEFAULT_VISIBILITY_DIRECTION = 0;
const DEFAULT_VISIBILITY_ANGLE = 70;
const MIN_VISIBILITY_ANGLE = 20;
const MAX_VISIBILITY_ANGLE = 180;
const CAMERA_ACCENT_COLOR = '#ff2d95';
const SELECTED_CAMERA_COLOR = '#1da1ff';
const CURRENT_CAMERA_COLOR = '#d32f2f';
const MARKER_DRAG_THRESHOLD = 6;

const getCsrfToken = () => {
    if (typeof document === 'undefined') {
        return '';
    }

    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
};

const clampNormalized = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return 0;
    }

    return Math.min(1, Math.max(0, numeric));
};

const normalizeVisibilityDirection = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return DEFAULT_VISIBILITY_DIRECTION;
    }

    return ((numeric % 360) + 360) % 360;
};

const normalizeVisibilityAngle = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return DEFAULT_VISIBILITY_ANGLE;
    }

    return Math.min(MAX_VISIBILITY_ANGLE, Math.max(MIN_VISIBILITY_ANGLE, numeric));
};

/**
 * Keep visibility settings valid whether they come from legacy placements or new edits.
 */
const normalizeVisibilitySettings = (visibility) => {
    const source = visibility && typeof visibility === 'object' && !Array.isArray(visibility) ? visibility : {};

    return {
        direction: normalizeVisibilityDirection(source.direction),
        angle: normalizeVisibilityAngle(source.angle),
    };
};

/**
 * Preserve arbitrary placement metadata while guaranteeing a usable visibility cone payload.
 */
const normalizePlacementMetadata = (metadata) => {
    const base = metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};

    return {
        ...base,
        visibility: normalizeVisibilitySettings(base.visibility),
    };
};

const getPlacementVisibility = (placement) => normalizePlacementMetadata(placement?.metadata).visibility;

const normalizePlacementRecord = (placement) => ({
    ...placement,
    metadata: normalizePlacementMetadata(placement?.metadata),
});

const normalizeMapRecord = (mapRecord) => ({
    ...mapRecord,
    placements: Array.isArray(mapRecord?.placements) ? mapRecord.placements.map(normalizePlacementRecord) : [],
});

/**
 * Build a simple sector path so each camera can render a directional visibility cone on the map.
 */
const buildVisibilityConePath = (radius, angleDegrees) => {
    const safeRadius = Math.max(1, Number(radius) || 1);
    const safeAngle = normalizeVisibilityAngle(angleDegrees);
    const halfAngle = (safeAngle / 2) * (Math.PI / 180);
    const center = safeRadius;
    const startX = center + (safeRadius * Math.cos(-halfAngle));
    const startY = center + (safeRadius * Math.sin(-halfAngle));
    const endX = center + (safeRadius * Math.cos(halfAngle));
    const endY = center + (safeRadius * Math.sin(halfAngle));

    return `M ${center} ${center} L ${startX.toFixed(3)} ${startY.toFixed(3)} A ${safeRadius} ${safeRadius} 0 0 1 ${endX.toFixed(3)} ${endY.toFixed(3)} Z`;
};

const buildUrlWithGroup = (baseUrl, groupName) => {
    try {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('group', groupName);
        return url.toString();
    } catch (error) {
        return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}group=${encodeURIComponent(groupName)}`;
    }
};

const buildRouteFromTemplate = (template, value) => {
    return (template || '').replace('__ID__', String(value)).replace('__ITEM__', String(value));
};

const parseErrorPayload = async (response) => {
    const payload = await response.json().catch(() => ({}));

    if (typeof payload.error === 'string' && payload.error) {
        throw new Error(payload.error);
    }

    if (typeof payload.message === 'string' && payload.message) {
        throw new Error(payload.message);
    }

    if (payload.errors && typeof payload.errors === 'object') {
        const firstEntry = Object.values(payload.errors)[0];
        if (Array.isArray(firstEntry) && firstEntry[0]) {
            throw new Error(firstEntry[0]);
        }
    }

    throw new Error('Request failed.');
};

const mapContainsItem = (mapRecord, itemId) => {
    return !!mapRecord?.placements?.some((placement) => Number(placement.item_id) === Number(itemId));
};

const updateMapRecord = (maps, activeMapId, mutateRecord) => {
    return maps.map((entry) => {
        if (Number(entry.id) !== Number(activeMapId)) {
            return entry;
        }

        return mutateRecord(entry);
    });
};

const findMapById = (maps, activeMapId) => {
    return maps.find((entry) => Number(entry.id) === Number(activeMapId)) || null;
};

const extractNormalizedCoords = (event, container) => {
    if (!container) {
        return null;
    }

    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) {
        return null;
    }

    const x = clampNormalized((event.clientX - rect.left) / rect.width);
    const y = clampNormalized((event.clientY - rect.top) / rect.height);

    return { x_scaled: x, y_scaled: y };
};

const buildAlarmWindowUrl = (baseUrl, alarmWindowStart, alarmWindowEnd) => {
    try {
        const url = new URL(baseUrl, window.location.origin);
        if (alarmWindowStart) {
            url.searchParams.set('startDateTime', alarmWindowStart);
        }
        if (alarmWindowEnd) {
            url.searchParams.set('endDateTime', alarmWindowEnd);
        }
        return url.toString();
    } catch (error) {
        const suffix = new URLSearchParams({
            ...(alarmWindowStart ? { startDateTime: alarmWindowStart } : {}),
            ...(alarmWindowEnd ? { endDateTime: alarmWindowEnd } : {}),
        }).toString();
        return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${suffix}`;
    }
};

const buildDefaultMapName = (hasMultipleLevels, maps) => {
    if (hasMultipleLevels) {
        return `Level ${maps.length + 1}`;
    }

    return `Area ${maps.length + 1}`;
};

const GroupMapDrawer = ({
    indexUrl,
    storeUrl,
    uploadUrl,
    settingsUrl,
    updateUrlTemplate,
    deleteUrlTemplate,
    defaultGroupName = '',
    mode = 'manage',
    canManage = false,
    currentItemId = null,
    alarmWindowStart = '',
    alarmWindowEnd = '',
    openFunctionName = '',
}) => {
    const imageFrameRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const createUploadButtonRef = useRef(null);
    const replaceUploadButtonRef = useRef(null);
    const createFileInputRef = useRef(null);
    const replaceFileInputRef = useRef(null);
    const createResumableRef = useRef(null);
    const replaceResumableRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [groupName, setGroupName] = useState(defaultGroupName);
    const [groupId, setGroupId] = useState(null);
    const [groupMetadata, setGroupMetadata] = useState({});
    const [items, setItems] = useState([]);
    const [maps, setMaps] = useState([]);
    const [activeMapId, setActiveMapId] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(currentItemId ? Number(currentItemId) : null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createUploadToken, setCreateUploadToken] = useState('');
    const [createUploadPreview, setCreateUploadPreview] = useState('');
    const [createDialogError, setCreateDialogError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [draggingItemId, setDraggingItemId] = useState(null);
    const [dragIntent, setDragIntent] = useState(null);
    const [dirtyMapIds, setDirtyMapIds] = useState([]);
    const [pendingUploadTokens, setPendingUploadTokens] = useState({});
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [zoomFactor, setZoomFactor] = useState(1);
    const [panState, setPanState] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [pendingCloseConfirmation, setPendingCloseConfirmation] = useState(false);
    const [displayMode, setDisplayMode] = useState('image');
    const [placementDrafts, setPlacementDrafts] = useState({});

    const hasMultipleLevels = !!groupMetadata?.has_multiple_levels;

    const activeMap = useMemo(() => findMapById(maps, activeMapId), [maps, activeMapId]);
    const itemsById = useMemo(() => new Map(items.map((entry) => [Number(entry.id), entry])), [items]);
    const selectedItem = useMemo(() => itemsById.get(Number(selectedItemId)) || null, [itemsById, selectedItemId]);
    const isCurrentItemSelection = useMemo(() => {
        return mode === 'item' && currentItemId && Number(selectedItemId) === Number(currentItemId);
    }, [currentItemId, mode, selectedItemId]);
    const gpsEnabledCount = useMemo(() => items.filter((entry) => entry.gps_enabled).length, [items]);
    const gpsItems = useMemo(() => {
        return items.filter((entry) => entry.gps_effective_latitude !== null && entry.gps_effective_longitude !== null);
    }, [items]);
    const hasGpsMapTab = gpsItems.length > 0;
    const placedItemIdsOnOtherLevels = useMemo(() => {
        if (!hasMultipleLevels || !activeMapId) {
            return new Set();
        }

        const identifiers = new Set();
        maps.forEach((mapRecord) => {
            if (Number(mapRecord.id) === Number(activeMapId)) {
                return;
            }

            (mapRecord.placements || []).forEach((placement) => {
                identifiers.add(Number(placement.item_id));
            });
        });

        return identifiers;
    }, [activeMapId, hasMultipleLevels, maps]);
    const baseScale = useMemo(() => {
        if (!activeMap?.image_width) {
            return 1;
        }

        return Math.min(1, 1100 / Number(activeMap.image_width || 1100));
    }, [activeMap]);
    const effectiveScale = useMemo(() => Number((baseScale * zoomFactor).toFixed(4)), [baseScale, zoomFactor]);
    const displayWidth = useMemo(() => {
        if (!activeMap?.image_width) {
            return null;
        }

        return Math.max(320, Math.round(Number(activeMap.image_width) * effectiveScale));
    }, [activeMap, effectiveScale]);
    const displayHeight = useMemo(() => {
        if (!activeMap?.image_height) {
            return null;
        }

        return Math.max(180, Math.round(Number(activeMap.image_height) * effectiveScale));
    }, [activeMap, effectiveScale]);
    const visibilityConeRadius = useMemo(() => {
        if (!displayWidth || !displayHeight) {
            return 60;
        }

        return Math.max(45, Math.round(Math.min(displayWidth, displayHeight) * 0.09));
    }, [displayHeight, displayWidth]);
    const gpsFrameUrl = useMemo(() => {
        if (!indexUrl || !groupName || !hasGpsMapTab || typeof window === 'undefined') {
            return '';
        }

        try {
            const url = new URL(indexUrl, window.location.origin);
            url.pathname = url.pathname.endsWith('/groups/maps') ? `${url.pathname}/gps-frame` : url.pathname.replace(/\/groups\/maps\/?$/, '/groups/maps/gps-frame');
            url.searchParams.set('group', groupName);
            if (groupId) {
                url.searchParams.set('group_id', String(groupId));
            }
            return url.toString();
        } catch (error) {
            const separator = indexUrl.includes('?') ? '&' : '?';
            return `${indexUrl.replace(/\/groups\/maps\/?$/, '/groups/maps/gps-frame')}${separator}group=${encodeURIComponent(groupName)}${groupId ? `&group_id=${encodeURIComponent(groupId)}` : ''}`;
        }
    }, [groupId, groupName, hasGpsMapTab, indexUrl]);

    const pushSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const uploadFileDirectly = useCallback(async (file, onSuccess) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('_token', getCsrfToken());

        const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
            credentials: 'same-origin',
            body: formData,
        });

        if (!response.ok) {
            await parseErrorPayload(response);
        }

        const payload = await response.json();
        onSuccess(payload);
    }, [uploadUrl]);

    const loadGroupMaps = useCallback(async (requestedGroupName, options = {}) => {
        if (!requestedGroupName) {
            setError('Group name is required.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(buildUrlWithGroup(indexUrl, requestedGroupName), {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                await parseErrorPayload(response);
            }

            const payload = await response.json();
            const nextItems = Array.isArray(payload.items) ? payload.items : [];
            const nextMaps = Array.isArray(payload.maps) ? payload.maps.map(normalizeMapRecord) : [];
            const fallbackMap = nextMaps[0] || null;
            const nextSelectedItemId = currentItemId
                ? Number(currentItemId)
                : (selectedItemId && nextItems.some((entry) => Number(entry.id) === Number(selectedItemId))
                    ? Number(selectedItemId)
                    : (nextItems[0] ? Number(nextItems[0].id) : null));

            setGroupId(payload.group_id ? Number(payload.group_id) : null);
            setGroupName(payload.group_name || requestedGroupName);
            setGroupMetadata(payload.group_metadata || {});
            setItems(nextItems);
            setMaps(nextMaps);
            setActiveMapId((current) => {
                if (current && nextMaps.some((entry) => Number(entry.id) === Number(current))) {
                    return current;
                }
                return fallbackMap ? Number(fallbackMap.id) : null;
            });
            setSelectedItemId(nextSelectedItemId);
            setDirtyMapIds([]);
            setPendingUploadTokens({});
            setPlacementDrafts({});
            setZoomFactor(1);
            setEditMode(false);
            setDisplayMode(nextMaps.length ? 'image' : (payload.items || []).some((entry) => entry.gps_effective_latitude !== null && entry.gps_effective_longitude !== null) ? 'gps' : 'image');
            if (options.openCreateDialog) {
                setCreateName(buildDefaultMapName(!!payload.group_metadata?.has_multiple_levels, nextMaps));
                setCreateDialogOpen(true);
            }
        } catch (loadError) {
            console.error('Failed to load group maps', loadError);
            setError(loadError.message || 'Unable to load group maps.');
            setItems([]);
            setMaps([]);
            setActiveMapId(null);
        } finally {
            setLoading(false);
        }
    }, [currentItemId, indexUrl, selectedItemId]);

    const openDrawer = useCallback((requestedGroupName = defaultGroupName, options = {}) => {
        if (!requestedGroupName) {
            pushSnackbar('This flux is not assigned to a group.', 'warning');
            return;
        }

        setOpen(true);
        loadGroupMaps(requestedGroupName, options);
    }, [defaultGroupName, loadGroupMaps, pushSnackbar]);

    const hasUnsavedChanges = useMemo(() => {
        return dirtyMapIds.length > 0 || !!createUploadToken || Object.keys(pendingUploadTokens).length > 0;
    }, [createUploadToken, dirtyMapIds, pendingUploadTokens]);

    const requestCloseDrawer = useCallback(() => {
        if (hasUnsavedChanges) {
            setPendingCloseConfirmation(true);
            return;
        }

        setOpen(false);
    }, [hasUnsavedChanges]);

    const navigateToItem = useCallback((itemId) => {
        const item = itemsById.get(Number(itemId));
        if (!item) {
            return;
        }

        if (mode === 'item' && currentItemId && Number(itemId) === Number(currentItemId)) {
            return;
        }

        setPendingNavigation({
            itemName: item.name,
            url: mode === 'alarm'
                ? buildAlarmWindowUrl(buildUrlWithGroup(item.alerts_url, groupName || ''), alarmWindowStart, alarmWindowEnd)
                : item.edit_url,
        });
    }, [alarmWindowEnd, alarmWindowStart, currentItemId, groupName, itemsById, mode]);

    const openGpsMapWindow = useCallback(() => {
        if (!gpsFrameUrl || typeof window === 'undefined') {
            return;
        }

        window.open(gpsFrameUrl, 'group-gps-map', 'noopener,noreferrer,width=1480,height=920');
    }, [gpsFrameUrl]);

    useEffect(() => {
        if (!openFunctionName) {
            return undefined;
        }

        window[openFunctionName] = (requestedGroupName, options = {}) => {
            openDrawer(requestedGroupName || defaultGroupName, options);
        };

        return () => {
            if (window[openFunctionName]) {
                delete window[openFunctionName];
            }
        };
    }, [defaultGroupName, openDrawer, openFunctionName]);

    useEffect(() => {
        if (!createDialogOpen) {
            setCreateUploadToken('');
            setCreateUploadPreview('');
            setCreateDialogError('');
        }
    }, [createDialogOpen]);

    useEffect(() => {
        if (!createDialogOpen) {
            return;
        }

        setCreateName((current) => current || buildDefaultMapName(hasMultipleLevels, maps));
    }, [createDialogOpen, hasMultipleLevels, maps]);

    useEffect(() => {
        if (!open || displayMode !== 'gps') {
            return undefined;
        }

        const handleMessage = (event) => {
            if (event.origin !== window.location.origin || !event.data || event.data.type !== 'aiv-group-gps-map:selected') {
                return;
            }

            const nextItemId = Number(event.data.itemId);
            if (!Number.isFinite(nextItemId)) {
                return;
            }

            if (mode === 'manage') {
                setSelectedItemId(nextItemId);
                return;
            }

            navigateToItem(nextItemId);
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [displayMode, mode, navigateToItem, open]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.Resumable || !uploadUrl || !canManage) {
            return undefined;
        }

        const csrfToken = getCsrfToken();

        const buildUploader = (onSuccess) => {
            const resumable = new window.Resumable({
                target: uploadUrl,
                chunkSize: 1 * 1024 * 1024,
                simultaneousUploads: 2,
                testChunks: false,
                throttleProgressCallbacks: 1,
                headers: {
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                query: { _token: csrfToken },
                fileType: ['jpg', 'jpeg', 'png', 'webp'],
            });

            if (!resumable.support) {
                return null;
            }

            resumable.on('fileAdded', () => {
                setUploading(true);
                setUploadProgress(0);
                setError('');
                setCreateDialogError('');
                resumable.upload();
            });
            resumable.on('progress', () => {
                setUploadProgress(Math.floor(resumable.progress() * 100));
            });
            resumable.on('fileSuccess', (_file, response) => {
                const payload = JSON.parse(response || '{}');
                onSuccess(payload);
            });
            resumable.on('fileError', (_file, message) => {
                setUploading(false);
                setUploadProgress(0);
                const uploadError = typeof message === 'string' && message ? message : 'Upload failed.';
                setError(uploadError);
                setCreateDialogError(uploadError);
            });
            resumable.on('complete', () => {
                setUploading(false);
                setUploadProgress(0);
            });

            return resumable;
        };

        createResumableRef.current?.cancel();
        replaceResumableRef.current?.cancel();

        createResumableRef.current = buildUploader((payload) => {
            setCreateUploadToken(payload.upload_token || '');
            setCreateUploadPreview(payload.preview_url || '');
            setCreateDialogError('');
            pushSnackbar('Map image uploaded. Create the level to save it.', 'success');
        });

        replaceResumableRef.current = buildUploader((payload) => {
            if (!activeMapId) {
                return;
            }

            setPendingUploadTokens((current) => ({
                ...current,
                [activeMapId]: payload.upload_token || '',
            }));
            setDirtyMapIds((current) => (current.includes(Number(activeMapId)) ? current : [...current, Number(activeMapId)]));
            pushSnackbar('Replacement image uploaded. Save the level to apply it.', 'success');
        });

        return () => {
            createResumableRef.current?.cancel();
            replaceResumableRef.current?.cancel();
        };
    }, [activeMapId, canManage, createDialogOpen, pushSnackbar, uploadUrl]);

    const handlePickedFile = useCallback(async (file, type) => {
        if (!file) {
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setError('');
        setCreateDialogError('');

        const onSuccess = (payload) => {
            if (type === 'create') {
                setCreateUploadToken(payload.upload_token || '');
                setCreateUploadPreview(payload.preview_url || '');
                pushSnackbar('Map image uploaded. Create the level to save it.', 'success');
            } else if (activeMapId) {
                setPendingUploadTokens((current) => ({
                    ...current,
                    [activeMapId]: payload.upload_token || '',
                }));
                setDirtyMapIds((current) => (current.includes(Number(activeMapId)) ? current : [...current, Number(activeMapId)]));
                pushSnackbar('Replacement image uploaded. Save the level to apply it.', 'success');
            }
        };

        try {
            const resumableInstance = type === 'create' ? createResumableRef.current : replaceResumableRef.current;
            if (resumableInstance) {
                resumableInstance.addFile(file);
            } else {
                await uploadFileDirectly(file, onSuccess);
                setUploading(false);
                setUploadProgress(0);
            }
        } catch (uploadError) {
            console.error('Failed to upload map image', uploadError);
            const message = uploadError.message || 'Upload failed.';
            setError(message);
            setCreateDialogError(message);
            setUploading(false);
            setUploadProgress(0);
        }
    }, [activeMapId, pushSnackbar, uploadFileDirectly]);

    const handleCreateFileSelection = useCallback(async (event) => {
        const file = event.target.files?.[0] || null;
        event.target.value = '';
        await handlePickedFile(file, 'create');
    }, [handlePickedFile]);

    const handleReplaceFileSelection = useCallback(async (event) => {
        const file = event.target.files?.[0] || null;
        event.target.value = '';
        await handlePickedFile(file, 'replace');
    }, [handlePickedFile]);

    const handleSettingsToggle = useCallback(async (checked) => {
        if (!groupName) {
            return;
        }

        setSaving(true);
        setError('');

        try {
            const response = await fetch(settingsUrl, {
                method: 'PATCH',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    group_id: groupId || undefined,
                    group: groupName,
                    metadata: {
                        has_multiple_levels: checked,
                    },
                }),
            });

            if (!response.ok) {
                await parseErrorPayload(response);
            }

            const payload = await response.json();
            setGroupMetadata(payload.group_metadata || {});
            pushSnackbar('Group settings updated.', 'success');
        } catch (settingsError) {
            console.error('Failed to update group settings', settingsError);
            setError(settingsError.message || 'Unable to update group settings.');
        } finally {
            setSaving(false);
        }
    }, [groupId, groupName, pushSnackbar]);

    useEffect(() => {
        if (!canManage || (!draggingItemId && !dragIntent)) {
            return undefined;
        }

        const handleMouseMove = (event) => {
            if (!draggingItemId && dragIntent) {
                const deltaX = event.clientX - dragIntent.startX;
                const deltaY = event.clientY - dragIntent.startY;
                if (Math.hypot(deltaX, deltaY) < MARKER_DRAG_THRESHOLD) {
                    return;
                }

                setDraggingItemId(dragIntent.itemId);
                setDragIntent((current) => (current?.itemId === dragIntent.itemId ? null : current));
                markMapDirty(activeMapId);
                return;
            }

            const coords = extractNormalizedCoords(event, imageFrameRef.current);
            if (!coords || !activeMapId) {
                return;
            }

            setMaps((previousMaps) => updateMapRecord(previousMaps, activeMapId, (record) => {
                const nextPlacements = Array.isArray(record.placements) ? [...record.placements] : [];
                const placementIndex = nextPlacements.findIndex((placement) => Number(placement.item_id) === Number(draggingItemId));
                if (placementIndex === -1) {
                    return record;
                }

                nextPlacements[placementIndex] = {
                    ...nextPlacements[placementIndex],
                    x_scaled: coords.x_scaled,
                    y_scaled: coords.y_scaled,
                };

                return { ...record, placements: nextPlacements };
            }));
        };

        const handleMouseUp = () => {
            setDraggingItemId(null);
            setDragIntent(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [activeMapId, canManage, dragIntent, draggingItemId]);

    useEffect(() => {
        if (!panState) {
            return undefined;
        }

        const handleMouseMove = (event) => {
            const container = scrollContainerRef.current;
            if (!container) {
                return;
            }

            const deltaX = event.clientX - panState.startX;
            const deltaY = event.clientY - panState.startY;

            container.scrollLeft = panState.scrollLeft - deltaX;
            container.scrollTop = panState.scrollTop - deltaY;
        };

        const handleMouseUp = () => {
            setPanState(null);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [panState]);

    const markMapDirty = (mapId) => {
        setDirtyMapIds((previous) => (previous.includes(Number(mapId)) ? previous : [...previous, Number(mapId)]));
    };

    const handlePlacementUpdate = (itemId, coords) => {
        if (!activeMapId || !coords || !editMode) {
            return;
        }

        const item = itemsById.get(Number(itemId));
        if (!item) {
            return;
        }

        setMaps((previousMaps) => updateMapRecord(previousMaps, activeMapId, (record) => {
            const placements = Array.isArray(record.placements) ? [...record.placements] : [];
            const existingIndex = placements.findIndex((placement) => Number(placement.item_id) === Number(itemId));
            const nextVisibility = existingIndex >= 0
                ? getPlacementVisibility(placements[existingIndex])
                : normalizeVisibilitySettings(placementDrafts[item.id]);
            const nextPlacement = {
                id: existingIndex >= 0 ? placements[existingIndex].id : null,
                item_id: Number(item.id),
                item_name: item.name,
                x_scaled: coords.x_scaled,
                y_scaled: coords.y_scaled,
                metadata: normalizePlacementMetadata({
                    ...(existingIndex >= 0 ? placements[existingIndex].metadata : {}),
                    visibility: nextVisibility,
                }),
            };

            if (existingIndex >= 0) {
                placements[existingIndex] = nextPlacement;
            } else {
                placements.push(normalizePlacementRecord(nextPlacement));
            }

            return { ...record, placements };
        }));

        markMapDirty(activeMapId);
    };

    const handleScrollContainerMouseDown = (event) => {
        if (event.button !== 1) {
            return;
        }

        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        event.preventDefault();
        setPanState({
            startX: event.clientX,
            startY: event.clientY,
            scrollLeft: container.scrollLeft,
            scrollTop: container.scrollTop,
        });
    };

    const handleWheelZoom = (event) => {
        if (!(event.ctrlKey || event.metaKey)) {
            return;
        }

        event.preventDefault();
        setZoomFactor((current) => {
            const next = event.deltaY < 0 ? current * 1.1 : current / 1.1;
            return Math.min(8, Math.max(0.5, Number(next.toFixed(2))));
        });
    };

    const handleImageClick = (event) => {
        if (!canManage || !selectedItemId || !editMode) {
            return;
        }

        const coords = extractNormalizedCoords(event, imageFrameRef.current);
        handlePlacementUpdate(selectedItemId, coords);
    };

    const handleMarkerMouseDown = (itemId, event) => {
        if (!canManage || !editMode || event.button !== 0) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setSelectedItemId(Number(itemId));
        setDragIntent({
            itemId: Number(itemId),
            startX: event.clientX,
            startY: event.clientY,
        });
    };

    const handleRemovePlacement = (itemId) => {
        if (!canManage || !activeMapId) {
            return;
        }

        setMaps((previousMaps) => updateMapRecord(previousMaps, activeMapId, (record) => ({
            ...record,
            placements: (record.placements || []).filter((placement) => Number(placement.item_id) !== Number(itemId)),
        })));
        markMapDirty(activeMapId);
    };

    const selectedPlacement = useMemo(() => {
        if (!activeMap || !selectedItemId) {
            return null;
        }

        return (activeMap.placements || []).find((placement) => Number(placement.item_id) === Number(selectedItemId)) || null;
    }, [activeMap, selectedItemId]);

    const selectedPlacementVisibility = useMemo(() => {
        if (selectedPlacement) {
            return getPlacementVisibility(selectedPlacement);
        }

        return normalizeVisibilitySettings(selectedItemId ? placementDrafts[selectedItemId] : null);
    }, [placementDrafts, selectedItemId, selectedPlacement]);

    const handleSelectedVisibilityChange = (field, value) => {
        if (!selectedItemId) {
            return;
        }

        const currentVisibility = selectedPlacement
            ? getPlacementVisibility(selectedPlacement)
            : normalizeVisibilitySettings(placementDrafts[selectedItemId]);
        const nextVisibility = normalizeVisibilitySettings({
            ...currentVisibility,
            [field]: Array.isArray(value) ? value[0] : value,
        });

        setPlacementDrafts((current) => ({
            ...current,
            [selectedItemId]: nextVisibility,
        }));

        if (!selectedPlacement || !activeMapId) {
            return;
        }

        setMaps((previousMaps) => updateMapRecord(previousMaps, activeMapId, (record) => {
            const placements = Array.isArray(record.placements) ? [...record.placements] : [];
            const placementIndex = placements.findIndex((placement) => Number(placement.item_id) === Number(selectedItemId));
            if (placementIndex === -1) {
                return record;
            }

            placements[placementIndex] = normalizePlacementRecord({
                ...placements[placementIndex],
                metadata: {
                    ...normalizePlacementMetadata(placements[placementIndex].metadata),
                    visibility: nextVisibility,
                },
            });

            return { ...record, placements };
        }));

        markMapDirty(activeMapId);
    };

    const handleMapNameChange = (event) => {
        const nextName = event.target.value;
        if (!activeMapId) {
            return;
        }

        setMaps((previousMaps) => updateMapRecord(previousMaps, activeMapId, (record) => ({
            ...record,
            name: nextName,
        })));
        markMapDirty(activeMapId);
    };

    const handleSaveMap = async () => {
        if (!activeMap || !dirtyMapIds.includes(Number(activeMap.id))) {
            return;
        }

        setSaving(true);
        setError('');

        try {
            const response = await fetch(buildRouteFromTemplate(updateUrlTemplate, activeMap.id), {
                method: 'PATCH',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    group_id: groupId || undefined,
                    name: activeMap.name,
                    sort_order: maps.findIndex((entry) => Number(entry.id) === Number(activeMap.id)),
                    upload_token: pendingUploadTokens[activeMap.id] || undefined,
                    placements: (activeMap.placements || []).map((placement) => ({
                        item_id: Number(placement.item_id),
                        x_scaled: clampNormalized(placement.x_scaled),
                        y_scaled: clampNormalized(placement.y_scaled),
                        metadata: normalizePlacementMetadata(placement.metadata),
                    })),
                }),
            });

            if (!response.ok) {
                await parseErrorPayload(response);
            }

            await response.json();
            pushSnackbar('Group map saved.', 'success');
            await loadGroupMaps(groupName);
        } catch (saveError) {
            console.error('Failed to save group map', saveError);
            setError(saveError.message || 'Unable to save group map.');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateMap = async () => {
        if (!groupName || !createName.trim() || !createUploadToken) {
            const dialogError = 'Map name and uploaded image are required.';
            setError(dialogError);
            setCreateDialogError(dialogError);
            return;
        }

        setSaving(true);
        setError('');
        setCreateDialogError('');

        try {
            const response = await fetch(storeUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    group_id: groupId || undefined,
                    group: groupName,
                    name: createName.trim(),
                    map_type: 'image',
                    upload_token: createUploadToken,
                }),
            });

            if (!response.ok) {
                await parseErrorPayload(response);
            }

            await response.json();
            setCreateDialogOpen(false);
            setCreateName('');
            setCreateUploadToken('');
            setCreateUploadPreview('');
            pushSnackbar('Group map created.', 'success');
            await loadGroupMaps(groupName);
        } catch (createError) {
            console.error('Failed to create group map', createError);
            const dialogError = createError.message || 'Unable to create group map.';
            setError(dialogError);
            setCreateDialogError(dialogError);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteMap = async () => {
        if (!activeMap) {
            return;
        }

        setSaving(true);
        setError('');

        try {
            const response = await fetch(buildRouteFromTemplate(deleteUrlTemplate, activeMap.id), {
                method: 'DELETE',
                headers: {
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                await parseErrorPayload(response);
            }

            await response.json();
            setDeleteDialogOpen(false);
            pushSnackbar('Group map deleted.', 'success');
            await loadGroupMaps(groupName);
        } catch (deleteError) {
            console.error('Failed to delete group map', deleteError);
            setError(deleteError.message || 'Unable to delete group map.');
        } finally {
            setSaving(false);
        }
    };

    const handleItemClick = (item) => {
        if (mode === 'manage') {
            setSelectedItemId(Number(item.id));
            return;
        }

        navigateToItem(item.id);
    };

    const sortedItems = useMemo(() => {
        return [...items].sort((left, right) => String(left.name).localeCompare(String(right.name)));
    }, [items]);

    const renderMarkers = () => {
        if (!activeMap || !Array.isArray(activeMap.placements)) {
            return null;
        }

        return activeMap.placements.map((placement) => {
            const item = itemsById.get(Number(placement.item_id));
            if (!item) {
                return null;
            }

            const isCurrentItem = currentItemId && Number(currentItemId) === Number(placement.item_id);
            const isSelected = Number(selectedItemId) === Number(placement.item_id);
            const isCurrentFlux = mode === 'item' && currentItemId && Number(placement.item_id) === Number(currentItemId);
            const showInlineLabel = isCurrentItem || isSelected || zoomFactor >= 1.5;
            const visibility = getPlacementVisibility(placement);
            const visibilityRotation = visibility.direction - 90;
            const visibilityPath = buildVisibilityConePath(visibilityConeRadius, visibility.angle);
            const gradientId = `visibility-cone-${activeMap.id}-${placement.item_id}`;
            const coneColor = isCurrentItem ? CURRENT_CAMERA_COLOR : (isSelected ? SELECTED_CAMERA_COLOR : CAMERA_ACCENT_COLOR);
            const markerBorderColor = isCurrentItem ? CURRENT_CAMERA_COLOR : (isSelected ? SELECTED_CAMERA_COLOR : '#ffffff');
            const markerGlow = isCurrentItem ? 'rgba(211,47,47,0.35)' : (isSelected ? 'rgba(29,161,255,0.45)' : 'rgba(255,45,149,0.35)');
            const markerBackground = isSelected ? 'rgba(29,161,255,0.14)' : 'rgba(255,255,255,0.94)';
            const markerIconColor = isCurrentItem ? CURRENT_CAMERA_COLOR : (isSelected ? SELECTED_CAMERA_COLOR : CAMERA_ACCENT_COLOR);

            const tooltipContent = (
                <Stack spacing={0.25}>
                    <Typography variant="subtitle2" sx={{ color: '#fff' }}>{item.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#fff' }}>Flux ID: {item.id}</Typography>
                    <Typography variant="caption" sx={{ color: '#fff' }}>Status: {item.status || 'unknown'}</Typography>
                    {item.gps_enabled ? <Typography variant="caption" sx={{ color: '#fff' }}>GPS enabled</Typography> : null}
                    <Typography variant="caption" sx={{ color: '#fff' }}>Direction: {Math.round(visibility.direction)}° · Angle: {Math.round(visibility.angle)}°</Typography>
                    <Typography variant="caption" sx={{ color: '#fff' }}>Double-click marker to open</Typography>
                </Stack>
            );

            return (
                <Tooltip key={`${activeMap.id}-${placement.item_id}`} title={tooltipContent} arrow placement="top">
                    <Box
                        onClick={(event) => {
                            event.stopPropagation();
                            setSelectedItemId(Number(placement.item_id));
                        }}
                        onDoubleClick={(event) => {
                            event.stopPropagation();
                            if (!isCurrentFlux) {
                                navigateToItem(placement.item_id);
                            }
                        }}
                        sx={{
                            position: 'absolute',
                            left: `${clampNormalized(placement.x_scaled) * 100}%`,
                            top: `${clampNormalized(placement.y_scaled) * 100}%`,
                            transform: 'translate(-50%, -50%)',
                            cursor: canManage && editMode ? 'grab' : 'pointer',
                            zIndex: isCurrentItem || isSelected ? 2 : 1,
                            overflow: 'visible',
                        }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                width: visibilityConeRadius * 2,
                                height: visibilityConeRadius * 2,
                                transform: `translate(-50%, -50%) rotate(${visibilityRotation}deg)`,
                                pointerEvents: 'none',
                                zIndex: 0,
                            }}>
                            <svg width={visibilityConeRadius * 2} height={visibilityConeRadius * 2} viewBox={`0 0 ${visibilityConeRadius * 2} ${visibilityConeRadius * 2}`}>
                                <defs>
                                    <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
                                        <stop offset="0%" stopColor={coneColor} stopOpacity={isSelected ? 0.34 : 0.28} />
                                        <stop offset="55%" stopColor={coneColor} stopOpacity={isSelected ? 0.24 : 0.2} />
                                        <stop offset="100%" stopColor={coneColor} stopOpacity="0.02" />
                                    </radialGradient>
                                </defs>
                                <path d={visibilityPath} fill={`url(#${gradientId})`} stroke={coneColor} strokeOpacity={isSelected || isCurrentItem ? 0.62 : 0.28} strokeWidth={isSelected || isCurrentItem ? 2.4 : 1.2} />
                                <line
                                    x1={visibilityConeRadius}
                                    y1={visibilityConeRadius}
                                    x2={visibilityConeRadius * 2}
                                    y2={visibilityConeRadius}
                                    stroke={coneColor}
                                    strokeOpacity={isSelected || isCurrentItem ? 0.42 : 0.25}
                                    strokeWidth={isSelected || isCurrentItem ? 1.6 : 1.2}
                                />
                            </svg>
                        </Box>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                bgcolor: markerBackground,
                                border: `3px solid ${markerBorderColor}`,
                                boxShadow: `0 10px 28px ${markerGlow}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: markerIconColor,
                                position: 'relative',
                                zIndex: 1,
                                cursor: canManage && editMode ? 'grab' : 'pointer',
                            }}>
                            <Box
                                onMouseDown={(event) => handleMarkerMouseDown(placement.item_id, event)}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                }}>
                                <CameraAltIcon sx={{ fontSize: 30 }} />
                            </Box>
                        </Box>
                        {canManage && editMode ? (
                            <Tooltip title="Remove placement">
                                <IconButton
                                    size="small"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleRemovePlacement(placement.item_id);
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        bottom: -8,
                                        right: -8,
                                        width: 20,
                                        height: 20,
                                        bgcolor: '#fff',
                                        border: '1px solid rgba(0,0,0,0.16)',
                                        color: '#d32f2f',
                                        '&:hover': {
                                            bgcolor: '#fff5f5',
                                        },
                                    }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {showInlineLabel ? (
                            <Box
                                sx={{
                                    mt: 0.5,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(0,0,0,0.72)',
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    maxWidth: 140,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    transform: 'translateX(-35%)',
                                }}>
                                {item.name}
                            </Box>
                        ) : null}
                    </Box>
                </Tooltip>
            );
        });
    };

    const renderVisibilityInspector = () => {
        if (!canManage || !selectedItem) {
            return null;
        }

        return (
            <Box
                sx={{
                    borderRadius: 2,
                    bgcolor: 'rgba(10, 15, 27, 0.92)',
                    color: '#fff',
                    boxShadow: '0 18px 50px rgba(0,0,0,0.24)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    p: 2,
                }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: { md: 250 } }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.1)',
                                border: `2px solid ${CAMERA_ACCENT_COLOR}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: CAMERA_ACCENT_COLOR,
                                flexShrink: 0,
                            }}>
                            <CameraAltIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>{selectedItem.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.74)' }}>
                                {selectedPlacement
                                    ? 'Adjust the placed camera cone here'
                                    : 'Set the cone first, then click the image to place the camera'}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ flex: 1 }}>
                        <Box
                            sx={{
                                width: 118,
                                height: 118,
                                borderRadius: '50%',
                                border: '1px solid rgba(255,255,255,0.16)',
                                bgcolor: 'rgba(255,255,255,0.03)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                            <svg width="104" height="104" viewBox="0 0 104 104" style={{ overflow: 'visible', transform: `rotate(${selectedPlacementVisibility.direction - 90}deg)` }}>
                                <defs>
                                    <radialGradient id="visibility-inspector-gradient" cx="50%" cy="50%" r="50%">
                                        <stop offset="0%" stopColor={CAMERA_ACCENT_COLOR} stopOpacity="0.42" />
                                        <stop offset="70%" stopColor={CAMERA_ACCENT_COLOR} stopOpacity="0.16" />
                                        <stop offset="100%" stopColor={CAMERA_ACCENT_COLOR} stopOpacity="0.03" />
                                    </radialGradient>
                                </defs>
                                <circle cx="52" cy="52" r="50" fill="none" stroke="rgba(255,255,255,0.12)" strokeDasharray="4 5" />
                                <path d={buildVisibilityConePath(52, selectedPlacementVisibility.angle)} fill="url(#visibility-inspector-gradient)" stroke={CAMERA_ACCENT_COLOR} strokeWidth="1.6" strokeOpacity="0.75" />
                                <line x1="52" y1="52" x2="104" y2="52" stroke={CAMERA_ACCENT_COLOR} strokeWidth="1.6" strokeOpacity="0.65" />
                                <circle cx="52" cy="52" r="5" fill={CAMERA_ACCENT_COLOR} fillOpacity="0.9" />
                            </svg>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.74)' }}>Rotation</Typography>
                                <Chip size="small" label={`${Math.round(selectedPlacementVisibility.direction)}°`} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
                            </Stack>
                            <Slider
                                size="small"
                                min={0}
                                max={359}
                                step={1}
                                value={selectedPlacementVisibility.direction}
                                onChange={(_, value) => handleSelectedVisibilityChange('direction', value)}
                                valueLabelDisplay="auto"
                                sx={{ color: CAMERA_ACCENT_COLOR, mb: 1.25 }}
                            />

                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.74)' }}>Angle</Typography>
                                <Chip size="small" label={`${Math.round(selectedPlacementVisibility.angle)}°`} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
                            </Stack>
                            <Slider
                                size="small"
                                min={MIN_VISIBILITY_ANGLE}
                                max={MAX_VISIBILITY_ANGLE}
                                step={1}
                                value={selectedPlacementVisibility.angle}
                                onChange={(_, value) => handleSelectedVisibilityChange('angle', value)}
                                valueLabelDisplay="auto"
                                sx={{ color: CAMERA_ACCENT_COLOR }}
                            />
                        </Box>
                    </Stack>
                </Stack>
            </Box>
        );
    };

    return (
        <>
            <Drawer anchor="right" open={open} onClose={requestCloseDrawer}>
                <Box sx={{ width: { xs: '100vw', md: '90vw' }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="h6">{groupName || 'Group map'}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {mode === 'alarm'
                                        ? 'Navigate to other cameras in the same group around the current alert time.'
                                        : (mode === 'item'
                                            ? 'Preview the camera location inside the current group.'
                                            : 'Manage floor maps and place each flux on the image.')}
                                </Typography>
                            </Box>
                            <IconButton onClick={requestCloseDrawer}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Box sx={{ px: 3, py: 2, flex: 1, overflow: 'auto' }}>
                        {loading ? (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading group maps...</Typography>
                            </Stack>
                        ) : null}

                        {!loading && error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                        {!loading && gpsEnabledCount > 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {gpsEnabledCount} {gpsEnabledCount === 1 ? 'flux has' : 'fluxes have'} GPS enabled. When coordinates exist, they are available in the GPS map tab with the same isolated map stack used by the working picker view.
                            </Alert>
                        ) : null}

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
                            <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
                                {hasGpsMapTab ? (
                                    <Tabs value={displayMode} onChange={(_, value) => setDisplayMode(value)} sx={{ mb: 2 }}>
                                        <Tab value="image" label="Uploaded maps" icon={<ImageIcon />} iconPosition="start" />
                                        <Tab value="gps" label="GPS map" icon={<PublicIcon />} iconPosition="start" />
                                    </Tabs>
                                ) : null}

                                {displayMode === 'image' ? (
                                    <>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="subtitle1">Levels</Typography>
                                    {canManage ? (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<AddPhotoAlternateIcon />}
                                            onClick={() => setCreateDialogOpen(true)}
                                            disabled={!hasMultipleLevels && maps.length > 0}
                                        >
                                            Add level
                                        </Button>
                                    ) : null}
                                </Stack>

                                {canManage ? (
                                    <FormControlLabel
                                        sx={{ mb: 1 }}
                                        control={
                                            <Switch
                                                checked={hasMultipleLevels}
                                                onChange={(event) => handleSettingsToggle(event.target.checked)}
                                                disabled={saving}
                                            />
                                        }
                                        label="Group has multiple levels / areas"
                                    />
                                ) : null}

                                {maps.length ? (
                                    <Tabs
                                        value={activeMapId || false}
                                        onChange={(_, value) => setActiveMapId(value)}
                                        orientation="vertical"
                                        variant="scrollable"
                                        sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, minHeight: 140 }}>
                                        {maps.map((mapRecord) => (
                                            <Tab
                                                key={mapRecord.id}
                                                value={Number(mapRecord.id)}
                                                label={
                                                    <Stack alignItems="flex-start" spacing={0.5} sx={{ py: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{mapRecord.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(mapRecord.placements || []).length} placements
                                                        </Typography>
                                                    </Stack>
                                                }
                                            />
                                        ))}
                                    </Tabs>
                                ) : (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        {canManage
                                            ? 'No floor maps exist for this group yet. Add the first image level to start placing cameras.'
                                            : 'No image map is configured for this group yet.'}
                                    </Alert>
                                )}

                                {canManage && !hasMultipleLevels && maps.length > 0 ? (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Multiple levels are disabled for this group. Turn the switch on if you want to add more than one level or area.
                                    </Alert>
                                ) : null}

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle1" sx={{ mb: 1 }}>Fluxes</Typography>
                                <List dense sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, maxHeight: 460, overflow: 'auto' }}>
                                    {sortedItems.filter((item) => {
                                        if (!hasMultipleLevels) {
                                            return true;
                                        }

                                        return !placedItemIdsOnOtherLevels.has(Number(item.id)) || mapContainsItem(activeMap, item.id);
                                    }).map((item) => {
                                        const isPlaced = mapContainsItem(activeMap, item.id);
                                        const isSelected = Number(selectedItemId) === Number(item.id);
                                        const isCurrentFlux = mode === 'item' && currentItemId && Number(item.id) === Number(currentItemId);
                                        const secondary = mode === 'manage'
                                            ? (isPlaced ? 'Placed on current level' : 'Click the image to place')
                                            : (isCurrentFlux ? 'Current camera' : (mode === 'alarm' ? 'Open this camera around the current alert time' : 'Open this camera'));

                                        return (
                                            <ListItemButton key={item.id} selected={isSelected} disabled={isCurrentFlux} onClick={() => handleItemClick(item)}>
                                                <ListItemText primary={item.name} secondary={secondary} />
                                                <Chip size="small" color={isCurrentFlux ? 'info' : (isPlaced ? 'success' : 'default')} label={isCurrentFlux ? 'Current' : (isPlaced ? 'Placed' : 'Missing')} />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>

                                {mode === 'manage' && selectedItem ? (
                                    <Stack spacing={1} sx={{ mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Selected flux: <strong>{selectedItem.name}</strong>
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="outlined" startIcon={<LaunchIcon />} onClick={() => navigateToItem(selectedItem.id)} disabled={isCurrentItemSelection}>
                                                Open flux
                                            </Button>
                                            <Button size="small" variant="outlined" startIcon={<PlaceIcon />} onClick={() => setPendingNavigation({
                                                itemName: selectedItem.name,
                                                url: selectedItem.alerts_url,
                                            })} disabled={isCurrentItemSelection}>
                                                Alerts
                                            </Button>
                                        </Stack>
                                        {mapContainsItem(activeMap, selectedItem.id) ? (
                                            <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => handleRemovePlacement(selectedItem.id)}>
                                                Remove from current level
                                            </Button>
                                        ) : null}
                                    </Stack>
                                ) : null}
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>GPS Cameras</Typography>
                                        <List dense sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, maxHeight: 520, overflow: 'auto' }}>
                                            {gpsItems.map((item) => {
                                                const isSelected = Number(selectedItemId) === Number(item.id);
                                                return (
                                                    <ListItemButton key={item.id} selected={isSelected} onClick={() => handleItemClick(item)}>
                                                        <ListItemText
                                                            primary={item.name}
                                                            secondary={`${Number(item.gps_effective_latitude).toFixed(6)}, ${Number(item.gps_effective_longitude).toFixed(6)}${item.gps_position_source ? ` · ${item.gps_position_source}` : ''}`}
                                                        />
                                                        <Chip size="small" color="success" label="GPS" />
                                                    </ListItemButton>
                                                );
                                            })}
                                        </List>
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            This tab runs in an isolated frame so page CSS cannot override the working Leaflet layout. To change a flux GPS point, edit the flux and use the GPS picker.
                                        </Alert>
                                        <Button sx={{ mt: 1 }} size="small" variant="outlined" startIcon={<LaunchIcon />} onClick={openGpsMapWindow} disabled={!gpsFrameUrl}>
                                            Open GPS map in separate window
                                        </Button>
                                    </>
                                )}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {displayMode === 'image' ? (activeMap ? (
                                    <Stack spacing={2}>
                                        {canManage ? (
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                                <TextField
                                                    label="Level name"
                                                    size="small"
                                                    value={activeMap.name || ''}
                                                    onChange={handleMapNameChange}
                                                    sx={{ minWidth: 220 }}
                                                />
                                                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveMap} disabled={saving || !dirtyMapIds.includes(Number(activeMap.id))}>
                                                    Save current level
                                                </Button>
                                                <Button
                                                    variant={editMode ? 'contained' : 'outlined'}
                                                    color={editMode ? 'warning' : 'primary'}
                                                    startIcon={editMode ? <EditLocationAltIcon /> : <LockOutlinedIcon />}
                                                    onClick={() => setEditMode((current) => !current)}>
                                                    {editMode ? 'Editing pins' : 'Pins locked'}
                                                </Button>
                                                <Button variant="outlined" ref={replaceUploadButtonRef} onClick={() => replaceFileInputRef.current?.click()} disabled={saving || uploading}>
                                                    Replace image
                                                </Button>
                                                <input ref={replaceFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleReplaceFileSelection} />
                                                <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteDialogOpen(true)} disabled={saving}>
                                                    Delete level
                                                </Button>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
                                                    <Button size="small" onClick={() => setZoomFactor((current) => Math.max(0.5, Number((current / 1.25).toFixed(2))))}>-</Button>
                                                    <Chip size="small" label={`Zoom ${Math.round(effectiveScale * 100)}%`} />
                                                    <Button size="small" onClick={() => setZoomFactor((current) => Math.min(8, Number((current * 1.25).toFixed(2))))}>+</Button>
                                                    <Button size="small" onClick={() => setZoomFactor(1)}>Reset</Button>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: 'flex-end' }}>
                                                <Button size="small" onClick={() => setZoomFactor((current) => Math.max(0.5, Number((current / 1.25).toFixed(2))))}>-</Button>
                                                <Chip size="small" label={`Zoom ${Math.round(effectiveScale * 100)}%`} />
                                                <Button size="small" onClick={() => setZoomFactor((current) => Math.min(8, Number((current * 1.25).toFixed(2))))}>+</Button>
                                                <Button size="small" onClick={() => setZoomFactor(1)}>Reset</Button>
                                            </Stack>
                                        )}

                                        {canManage ? (
                                            <Alert severity="info">
                                                {editMode
                                                    ? 'Edit mode is active. Select a flux from the left, then click the image to place it. Click a marker to select it, drag only after you start moving it, adjust its view cone from the floating map inspector, and double-click a marker to open that camera. Use Ctrl plus mouse wheel to zoom and middle click plus drag to pan the map.'
                                                    : 'Pins are locked. Turn on editing when you want to place or move camera markers, or tune each camera view cone. Double-click a marker to open that camera. Use Ctrl plus mouse wheel to zoom and middle click plus drag to pan the map.'}
                                            </Alert>
                                        ) : null}

                                        {uploading ? <Alert severity="info">Uploading image... {uploadProgress}%</Alert> : null}
                                        {pendingUploadTokens[activeMap?.id] ? <Alert severity="warning">A replacement image was uploaded. Save the level to apply it.</Alert> : null}
                                        {renderVisibilityInspector()}

                                        {activeMap.image_url ? (
                                            <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2, p: 2, bgcolor: '#fafafa' }}>
                                                <Box
                                                    ref={scrollContainerRef}
                                                    onMouseDown={handleScrollContainerMouseDown}
                                                    onWheel={handleWheelZoom}
                                                    sx={{
                                                        display: 'block',
                                                        overflow: 'auto',
                                                        maxHeight: '72vh',
                                                        cursor: panState ? 'grabbing' : 'default',
                                                    }}>
                                                    <Box
                                                        ref={imageFrameRef}
                                                        onClick={handleImageClick}
                                                        sx={{
                                                            position: 'relative',
                                                            display: 'inline-block',
                                                            lineHeight: 0,
                                                            cursor: canManage && selectedItemId && editMode ? 'crosshair' : 'default',
                                                            width: displayWidth || 'auto',
                                                            height: displayHeight || 'auto',
                                                        }}>
                                                        <img
                                                            src={activeMap.image_url}
                                                            alt={activeMap.name}
                                                            style={{
                                                                display: 'block',
                                                                borderRadius: 8,
                                                                width: displayWidth || '100%',
                                                                height: displayHeight || 'auto',
                                                                maxWidth: 'none',
                                                            }}
                                                        />
                                                        <Box sx={{ position: 'absolute', inset: 0 }}>
                                                            {renderMarkers()}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Alert severity="warning">This level does not have an image yet.</Alert>
                                        )}
                                    </Stack>
                                ) : null) : (
                                    <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2, p: 2, bgcolor: '#fafafa' }}>
                                        {gpsFrameUrl ? (
                                            <Box
                                                component="iframe"
                                                src={gpsFrameUrl}
                                                title={`GPS map for ${groupName || 'group'}`}
                                                sx={{
                                                    width: '100%',
                                                    height: '72vh',
                                                    minHeight: 520,
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    border: '1px solid rgba(0,0,0,0.12)',
                                                    backgroundColor: '#eef2f6',
                                                }}
                                            />
                                        ) : (
                                            <Alert severity="warning">GPS coordinates exist, but the isolated GPS map could not be prepared for this group.</Alert>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </Drawer>

            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add level map</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {createDialogError ? <Alert severity="error">{createDialogError}</Alert> : null}
                        <TextField label="Level name" value={createName} onChange={(event) => setCreateName(event.target.value)} fullWidth />
                        <Button variant="outlined" ref={createUploadButtonRef} onClick={() => createFileInputRef.current?.click()} startIcon={<AddPhotoAlternateIcon />} disabled={uploading}>
                            {createUploadToken ? 'Image uploaded' : 'Upload image'}
                        </Button>
                        <input ref={createFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleCreateFileSelection} />
                        {uploading ? <Alert severity="info">Uploading image... {uploadProgress}%</Alert> : null}
                        {createUploadPreview ? (
                            <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, p: 1 }}>
                                <img src={createUploadPreview} alt="Uploaded preview" style={{ maxWidth: '100%', maxHeight: 240, display: 'block', margin: '0 auto' }} />
                            </Box>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateMap} disabled={saving}>Create</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete level</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Delete <strong>{activeMap?.name || 'this level'}</strong> and all saved placements on it?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteMap} disabled={saving}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pendingCloseConfirmation} onClose={() => setPendingCloseConfirmation(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Discard unsaved changes?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        You have unsaved group map changes. Do you want to discard them or go back and save first?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingCloseConfirmation(false)}>Keep editing</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setPendingCloseConfirmation(false);
                            setCreateDialogOpen(false);
                            setCreateUploadToken('');
                            setCreateUploadPreview('');
                            setDirtyMapIds([]);
                            setPendingUploadTokens({});
                            setOpen(false);
                        }}>
                        Discard changes
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!pendingNavigation} onClose={() => setPendingNavigation(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Leave current page?</DialogTitle>
                <DialogContent>
                    <Stack spacing={1}>
                        <Typography variant="body2">
                            Open <strong>{pendingNavigation?.itemName || 'this camera'}</strong> and leave the current page?
                        </Typography>
                        {hasUnsavedChanges ? (
                            <Alert severity="warning">
                                You have unsaved group map changes. Save them first or continue and discard them.
                            </Alert>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingNavigation(null)}>Cancel</Button>
                    {hasUnsavedChanges && activeMap && dirtyMapIds.includes(Number(activeMap.id)) ? (
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                const targetUrl = pendingNavigation?.url;
                                await handleSaveMap();
                                setPendingNavigation(null);
                                if (targetUrl) {
                                    window.location.href = targetUrl;
                                }
                            }}>
                            Save and continue
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        onClick={() => {
                            const targetUrl = pendingNavigation?.url;
                            setPendingNavigation(null);
                            if (targetUrl) {
                                window.location.href = targetUrl;
                            }
                        }}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar((current) => ({ ...current, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default GroupMapDrawer;
